import React from 'react';
import PropTypes from 'prop-types';
import { LuBoxes, LuPackage, LuUser, LuUserX } from 'react-icons/lu';
import useStockText from '../stockText';
import { nameInitials } from './stockBits';

const PALETTE = ['#6366f1', '#8b5cf6', '#ec4899', '#f43f5e', '#f97316', '#eab308', '#22c55e', '#06b6d4', '#3b82f6', '#14b8a6'];

/** Same colour per name, as before. Inline colours go through CSS variables (dark-mode shield). */
export const avatarColor = (name) => PALETTE[(name || '').charCodeAt(0) % PALETTE.length];
const colorVars = (name) => {
  const color = avatarColor(name);
  return { '--avatar': color, '--avatar-soft': `${color}22` };
};

const Avatar = ({ name, size = 'md' }) => (
  <span
    aria-hidden="true"
    style={colorVars(name)}
    className={`tw-grid tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-[color:var(--avatar)] tw-font-bold tw-text-white ${size === 'sm' ? 'tw-h-7 tw-w-7 tw-text-[10px]' : 'tw-h-10 tw-w-10 tw-text-xs'}`}
  >
    {name ? nameInitials(name) : <LuUser className="tw-h-4 tw-w-4" />}
  </span>
);

Avatar.propTypes = { name: PropTypes.string, size: PropTypes.oneOf(['sm', 'md']) };

const ITEM = 'tw-flex tw-items-center tw-justify-between tw-gap-3 tw-rounded-xl tw-px-2.5 tw-py-2 tw-text-sm hover:tw-bg-slate-900/[0.03] dark:hover:tw-bg-white/[0.04]';
const CARD = 'tw-flex tw-min-w-0 tw-flex-col tw-rounded-2xl tw-border tw-bg-white/80 tw-p-3.5 dark:tw-bg-white/[0.03]';

const Amount = ({ quantity, date }) => {
  const { label, formatDateTime } = useStockText();
  return (
    <span className="tw-flex tw-shrink-0 tw-flex-col tw-items-end">
      <span className="tw-font-semibold tw-tabular-nums tw-text-slate-800 dark:tw-text-slate-100">{label('assigned.units', { quantity })}</span>
      <span className="tw-text-[11px] tw-tabular-nums tw-text-slate-400 dark:tw-text-slate-500">{formatDateTime(date)}</span>
    </span>
  );
};

Amount.propTypes = { quantity: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), date: PropTypes.string };

/** An employee and the stocks given to them (search narrows the list). */
export const EmployeeCard = ({ employee, search }) => {
  const { label } = useStockText();
  const hasAssignment = employee.assignments.length > 0;
  const totalQty = employee.assignments.reduce((s, a) => s + (parseFloat(a.quantity) || 0), 0);
  const term = search.toLocaleLowerCase('tr');
  const visible = search ? employee.assignments.filter((a) => (a.stockName || '').toLocaleLowerCase('tr').includes(term)) : employee.assignments;

  return (
    <article className={`${CARD} ${hasAssignment ? 'tw-border-slate-200/80 dark:tw-border-white/10' : 'tw-border-dashed tw-border-slate-300 tw-opacity-80 dark:tw-border-white/15'}`}>
      <header className="tw-flex tw-items-center tw-gap-3">
        <Avatar name={employee.employeeName} />
        <span className="tw-min-w-0 tw-flex-1">
          <span className="tw-block tw-truncate tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">{employee.employeeName}</span>
          <span className="tw-block tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
            {hasAssignment ? label('assigned.employeeMeta', { count: employee.assignments.length, quantity: totalQty }) : label('assigned.none')}
          </span>
        </span>
        <span style={colorVars(employee.employeeName)} className="tw-grid tw-h-7 tw-min-w-[1.75rem] tw-place-items-center tw-rounded-full tw-bg-[color:var(--avatar-soft)] tw-px-2 tw-text-xs tw-font-bold tw-tabular-nums tw-text-[color:var(--avatar)]">
          {employee.assignments.length}
        </span>
      </header>
      <div className="tw-mt-3 tw-space-y-0.5 tw-border-t tw-border-slate-200/70 tw-pt-2 dark:tw-border-white/10">
        {hasAssignment ? visible.map((a, index) => (
          <div key={a.id || index} className={ITEM}>
            <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-2 tw-text-slate-700 dark:tw-text-slate-200">
              <LuPackage aria-hidden="true" className="tw-h-4 tw-w-4 tw-shrink-0 tw-text-slate-400" />
              <span className="tw-truncate">{a.stockName}</span>
            </span>
            <Amount quantity={a.quantity} date={a.date} />
          </div>
        )) : (
          <p className="tw-flex tw-items-center tw-gap-2 tw-px-2.5 tw-py-2 tw-text-sm tw-text-slate-400 dark:tw-text-slate-500">
            <LuUserX aria-hidden="true" className="tw-h-4 tw-w-4" />
            {label('assigned.noItems')}
          </p>
        )}
      </div>
    </article>
  );
};

EmployeeCard.propTypes = { employee: PropTypes.object.isRequired, search: PropTypes.string.isRequired };

/** A stock and the employees it was given to (search narrows the list). */
export const StockCard = ({ stock, search }) => {
  const { label } = useStockText();
  const totalQty = stock.assignments.reduce((sum, a) => sum + (parseFloat(a.quantity) || 0), 0);
  const term = search.toLocaleLowerCase('tr');
  const visible = search ? stock.assignments.filter((a) => (a.employeeName || '').toLocaleLowerCase('tr').includes(term)) : stock.assignments;

  return (
    <article className={`${CARD} tw-border-slate-200/80 dark:tw-border-white/10`}>
      <header className="tw-flex tw-items-center tw-gap-3">
        <span className="tw-grid tw-h-10 tw-w-10 tw-shrink-0 tw-place-items-center tw-rounded-xl tw-bg-gradient-to-br tw-from-teal-400 tw-to-cyan-600 tw-text-white tw-shadow-md tw-shadow-teal-500/25">
          <LuBoxes aria-hidden="true" className="tw-h-5 tw-w-5" />
        </span>
        <span className="tw-min-w-0 tw-flex-1">
          <span className="tw-block tw-truncate tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">{stock.stockName}</span>
          <span className="tw-block tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label('assigned.stockMeta', { count: stock.assignments.length, quantity: totalQty })}</span>
        </span>
        <span className="tw-shrink-0 tw-rounded-full tw-bg-teal-500/10 tw-px-2.5 tw-py-1 tw-text-xs tw-font-bold tw-tabular-nums tw-text-teal-700 dark:tw-text-teal-300">
          {label('assigned.units', { quantity: totalQty })}
        </span>
      </header>
      <div className="tw-mt-3 tw-space-y-0.5 tw-border-t tw-border-slate-200/70 tw-pt-2 dark:tw-border-white/10">
        {visible.map((a, index) => (
          <div key={a.id || index} className={ITEM}>
            <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-2 tw-text-slate-700 dark:tw-text-slate-200">
              <Avatar name={a.employeeName} size="sm" />
              <span className="tw-truncate">{a.employeeName}</span>
            </span>
            <Amount quantity={a.quantity} date={a.date} />
          </div>
        ))}
      </div>
    </article>
  );
};

StockCard.propTypes = { stock: PropTypes.object.isRequired, search: PropTypes.string.isRequired };
